import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperclip,
  faSave,
  faTimes,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

interface CommentFormProps {
  onSubmit: (data: { message: string; attachments: File[] }) => void;
  onCancel?: () => void;
  initialMessage?: string;
  isEditing?: boolean;
  loading?: boolean;
}

const CommentForm: React.FC<CommentFormProps> = ({
  onSubmit,
  onCancel,
  initialMessage = "",
  isEditing = false,
  loading = false,
}) => {
  const [message, setMessage] = useState(initialMessage);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMessage(initialMessage);
  }, [initialMessage]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments([...attachments, ...Array.from(e.target.files)]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      onSubmit({ message, attachments });
      setMessage("");
      setAttachments([]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border rounded-lg p-4 shadow-sm space-y-4 w-full"
    >
      <textarea
        className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none resize-none"
        placeholder="Write your comment here..."
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={loading}
      />

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="flex flex-col space-y-2 text-sm text-gray-700">
          {attachments.map((file, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between bg-gray-50 px-3 py-1 rounded"
            >
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                className="text-red-500 hover:text-red-700"
                onClick={() => removeAttachment(idx)}
                title="Remove"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center text-blue-500 hover:text-blue-700"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faPaperclip} className="mr-2" />
            Attach File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={handleFileChange}
            disabled={loading}
          />
        </div>

        <div className="flex space-x-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center text-gray-500 hover:text-gray-700 px-3 py-1"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !message.trim()}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                {isEditing ? "Update" : "Comment"}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CommentForm;